import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords = '', 
  image = '/favicon.ico',
  url = '',
  type = 'website',
  article = null,
  organization = null
}) => {
  const siteName = 'SharpeMetrix';
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const fullUrl = url ? `https://sharpemetrix.com${url}` : 'https://sharpemetrix.com';
  const fullImage = image.startsWith('http') ? image : `https://sharpemetrix.com${image}`;

  // Default organization schema
  const defaultOrganization = {
    "@context": "https://schema.org",
    "@type": "FinancialService",
    "name": "SharpeMetrix",
    "description": "Alternative data-driven investment management platform for financial advisors",
    "url": "https://sharpemetrix.com",
    "logo": "https://sharpemetrix.com/logo_wordmark.svg",
    "sameAs": [
      "https://www.linkedin.com/company/sharpemetrix"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "url": "https://sharpemetrix.com/contact"
    },
    "areaServed": "US",
    "serviceType": "Investment Management",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Investment Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Model Portfolios",
            "description": "Systematic, alternative data-driven model portfolios"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Power List Rankings",
            "description": "Proprietary stock rankings based on alternative data signals"
          }
        }
      ]
    }
  };

  // Create JSON-LD structured data
  const structuredData = organization || defaultOrganization;

  // Article schema for blog/content pages
  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": fullTitle,
    "description": description,
    "image": fullImage,
    "url": fullUrl,
    "datePublished": article.datePublished,
    "dateModified": article.dateModified || article.datePublished,
    "author": {
      "@type": "Organization",
      "name": "SharpeMetrix"
    },
    "publisher": {
      "@type": "Organization", 
      "name": "SharpeMetrix",
      "logo": {
        "@type": "ImageObject",
        "url": "https://sharpemetrix.com/logo_wordmark.svg"
      }
    }
  } : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="SharpeMetrix - Alternative Data-Driven Investment Management" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />
      <meta property="twitter:image:alt" content="SharpeMetrix - Alternative Data-Driven Investment Management" />
      <meta property="twitter:site" content="@SharpeMetrix" />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content={siteName} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <link rel="canonical" href={fullUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Article Schema if provided */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO; 